import { memo, useState } from 'react';
import { useSalesEntry, SalesEntryItem, PaymentEntryItem } from 'contexts/SalesEntryContext';
import { ItemModal } from './sections/ItemModal';
import { CustomerModal } from './sections/CustomerModal';
import { SalesPersonModal } from './sections/SalesPersonModal';
import { RedemptionModal } from './sections/RedemptionModal';
import { RewardPointsModal } from './sections/RewardPointsModal';
import { CouponModal } from './sections/CouponModal';
import { DiscountModal } from './sections/DiscountModal';
import { PaymentModal } from './sections/PaymentModal';
import { OldGoldModal } from './sections/OldGoldModal';

type SearchType = 'bagNo' | 'styleNo' | 'igiNo' | 'huid';

interface SalesEntryModalsProps {
  showItemModal: boolean;
  setShowItemModal: (show: boolean) => void;
  editingItem: string | null;
  showCustomerModal: boolean;
  setShowCustomerModal: (show: boolean) => void;
  showSalesPersonModal: boolean;
  setShowSalesPersonModal: (show: boolean) => void;
  showRedemptionModal: boolean;
  setShowRedemptionModal: (show: boolean) => void;
  showRewardPointsModal: boolean;
  setShowRewardPointsModal: (show: boolean) => void;
  showCouponModal: boolean;
  setShowCouponModal: (show: boolean) => void;
  showDiscountModal: boolean;
  setShowDiscountModal: (show: boolean) => void;
  showPaymentModal: boolean;
  setShowPaymentModal: (show: boolean) => void;
  showOldGoldModal: boolean;
  setShowOldGoldModal: (show: boolean) => void;
  defaultSearchType?: SearchType;
}

export const SalesEntryModals = memo<SalesEntryModalsProps>(({
  showItemModal,
  setShowItemModal,
  editingItem,
  showCustomerModal,
  setShowCustomerModal,
  showSalesPersonModal,
  setShowSalesPersonModal,
  showRedemptionModal,
  setShowRedemptionModal,
  showRewardPointsModal,
  setShowRewardPointsModal,
  showCouponModal,
  setShowCouponModal,
  showDiscountModal,
  setShowDiscountModal,
  showPaymentModal,
  setShowPaymentModal,
  showOldGoldModal,
  setShowOldGoldModal,
  defaultSearchType = 'styleNo'
}) => {
  const { state, actions } = useSalesEntry();

  const handleItemSubmit = (item: SalesEntryItem) => {
    if (editingItem) {
      actions.updateItem(editingItem, item);
    } else {
      const isDuplicate = state.items.some(item => item.id === item.id); // or item.bagNo === newItem.bagNo
      if (!isDuplicate) {
        actions.addItem(item);
      }
      else {
        console.warn('Duplicate product. Not adding again.');
      }
    }
  };

  const handleCustomerSelect = (customer: { id: string; name: string; phone: string; email: string; ShippingAddressId?:any; BillingAddressId?:any; } | null) => {
    actions.setCustomer(customer);
  };

  const handleSalesPersonSubmit = (salesPerson: { id: string; name: string; } | null) => {
    actions.setSalesPerson(salesPerson);
  };

  const handleRedemptionSubmit = (redemption: { points: number; amount: number; } | null) => {
    actions.setRedemption(redemption);
  };

  const handleRewardPointsSubmit = async (points: number) => {
    actions.setRewardPoints(points);
  };

  const handleCouponSubmit = (coupon: { id: string; code: string; discount: number; couponPrice: number; couponTypeId: number; couponCode: string | null; withGst: boolean | null; isConsumerOfferApplicable: boolean | null; } | null) => {
    actions.setCoupon(coupon);
  };

  const [metalKtId, setMetalKtId] = useState<number[]>([]);

  const handleSelectionChange = (selectedRows: any[]) => {
    const ids = selectedRows.map(row => row.metalKtId); // or row.id based on your structure
    setMetalKtId(ids);
  };

  const handlePaymentSubmit = (payments: PaymentEntryItem[] | null) => {
    // Clear existing payments and add new ones
    actions.clearPayments();
    if (payments) {

      payments.forEach(payment => actions.addPayment(payment));
    }
  };

  const handleCouponRemoveFromPayment = (couponCode: string, couponData: any) => {
    // Remove coupon from context when Gift Voucher payment is deleted
    if (state.coupon && state.coupon.code === couponCode) {
      actions.setCoupon(couponData);

      // Also remove any Gift Voucher payments with this code from context
      if (state.payment) {
        const filteredPayments = state.payment.filter(
          payment => !(payment.method === 'Gift Voucher' && payment.referenceNumber === couponCode)
        );

        actions.clearPayments();
        filteredPayments.forEach(payment => {
          actions.addPayment(payment);
        });
      }
    }
  };


  return (
    <>
      {/* Item Modal */}
      <ItemModal
        show={showItemModal}
        onHide={() => setShowItemModal(false)}
        editingItem={editingItem}
        items={state.items}
        onSubmit={handleItemSubmit}
        defaultSearchType={defaultSearchType}
      />

      {/* Customer Modal */}
      <CustomerModal
        show={showCustomerModal}
        onHide={() => setShowCustomerModal(false)}
        currentCustomer={state.customer}
        onCustomerSelect={handleCustomerSelect}
      />

      {/* Sales Person Modal */}
      <SalesPersonModal
        show={showSalesPersonModal}
        onHide={() => setShowSalesPersonModal(false)}
        currentSalesPerson={state.salesPerson}
        onSalesPersonSelect={handleSalesPersonSubmit}
      />

      {/* Redemption Modal */}
      <RedemptionModal
        show={showRedemptionModal}
        onHide={() => setShowRedemptionModal(false)}
        currentRedemption={state.redemption}
        onRedemptionSubmit={handleRedemptionSubmit}
      />

      {/* Reward Points Modal */}
      <RewardPointsModal
        show={showRewardPointsModal}
        onHide={() => setShowRewardPointsModal(false)}
        currentRewardPoints={state.rewardPoints}
        onRewardPointsSubmit={handleRewardPointsSubmit}
        customerId={state.customer?.id}
      />

      {/* Coupon Modal */}
      <CouponModal
        show={showCouponModal}
        onHide={() => setShowCouponModal(false)}
        currentCoupon={state.coupon}
        consumerOfferDetail={state.consumerOfferDetail || []}
        onCouponSubmit={handleCouponSubmit}
      />

      {/* Discount Modal */}
      <DiscountModal
        show={showDiscountModal}
        onHide={() => setShowDiscountModal(false)}
      />

      {/* Payment Modal */}
      <PaymentModal
        show={showPaymentModal}
        onHide={() => setShowPaymentModal(false)}
        currentPayment={state.payment}
        onPaymentSubmit={handlePaymentSubmit}
        onCouponRemove={handleCouponRemoveFromPayment}
      />

      {/* Old Gold Modal */}
      <OldGoldModal
        show={showOldGoldModal}
        onHide={() => setShowOldGoldModal(false)}
      />
    </>
  );
});